import "../../styles/global.css";
import "./Individual.css"; // custom styles for this page
import React, { useState, useEffect, useMemo } from "react";
import { fetchDemoQuestionAverageByStudent, fetchDemoClickSequenceByStudent, fetchDemoQuizCompletionByStudent, fetchDemoLatestClickByStudent } from "../../api/individualApi";
import { fetchDemoQuestionAverage, fetchAllDemoSubmissionDates } from "../../api/overviewApi";
import { Slider, Typography, Select, MenuItem } from "@mui/material";
import dayjs from "dayjs";
import RadarChart from "./RadarChart";
import { studentAliasMap } from "../Constants.js"; // for student name aliasing
import IndividualClickBarChart from "./IndividualClickBar"; // import the bar chart component
import TrendChart from "../Trend/TrendChart.js";

const Individuals = () => {

    const [studentQuestionAverage, setStudentQuestionAverage] = useState([]);
    const [questionAverage, setQuestionAverage] = useState([]);
    const [error, setError] = useState(null);
    const [allDates, setAllDates] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [selectedUser, setSelectedUser] = useState("");
    const [studentClickStats, setStudentClickStats] = useState([]);
    const [studentQuizCompletion, setStudentQuizCompletion] = useState(null);
    const [studentLatestClick, setStudentLatestClick] = useState(null);
    const [trendData, setTrendData] = useState([]);

    function generateDateRanges(minDate, maxDate, numBins = 10) {
        const step = (maxDate - minDate) / numBins;
        return Array.from({ length: numBins }, (_, i) => {
            const start = new Date(minDate + i * step).toISOString();
            const end = new Date(minDate + (i + 1) * step).toISOString();
            return [start, end];
        })
    }

    useEffect(() => {
        console.log("Fetching all demo submission dates...");
        fetchAllDemoSubmissionDates()
            .then((data) => {
                const timestamps = data.dates.map((d) => new Date(d).getTime());
                const minDate = Math.min(...timestamps);
                const maxDate = Math.max(...timestamps);
                setAllDates([minDate, maxDate]);
                setDateRange([minDate, maxDate]);
            })
            .catch((error) => {
                console.error("Error fetching all demo submission dates:", error);
            });
    }, []);

    useEffect(() => {
        if (dateRange[0] && dateRange[1]) {
            const [startISO, endISO] = dateRange.map((ts) => new Date(ts).toISOString());

            fetchDemoQuestionAverageByStudent(startISO, endISO)
                .then((data) => setStudentQuestionAverage(data?.student_question_stats ?? [])) // not sure.. double check
                .catch(() => setError("Failed to load student averages"));

            fetchDemoQuestionAverage(startISO, endISO)
                .then((data) => setQuestionAverage(data.questions ?? []))
                .catch(() => setError("Failed to load class averages"))

            fetchDemoClickSequenceByStudent(startISO, endISO)
                .then((data) => setStudentClickStats(data?.click_sequence_stats ?? []))
                .catch(() => setError("Failed to load student click sequences"));

            fetchDemoQuizCompletionByStudent(startISO, endISO)
                .then((data) => setStudentQuizCompletion(data?.student_question_completion ?? []))
                .catch(() => setError("Failed to load student quiz completion"));
            
            fetchDemoLatestClickByStudent(startISO, endISO)
                .then((data) => setStudentLatestClick(data?.latest_click_timestamps ?? []))
                .catch(() => setError("Failed to load student latest click"));
            
        }
    }, [dateRange]);

    useEffect(() => {
        if (allDates[0] && allDates[1]) {
            const [minDate, maxDate] = allDates;
            const dateRanges = generateDateRanges(minDate, maxDate);

            Promise.all(
                dateRanges.map(([start, end]) =>
                    fetchDemoQuestionAverageByStudent(start, end).then((res) => ({
                            start,
                            end,
                            // questions: data?.questions || [],
                            students: res?.student_question_stats || [],
                        }))
                )
            ).then((timeSeriesData) => {
                const trends = {};

                timeSeriesData.forEach(({ start, end, students }) => {
                    const timePoint = new Date((new Date(start).getTime() + new Date(end).getTime()) / 2).toISOString();

                    for (const student of students) {
                        const construct = student.group_code;
                        const avg = parseFloat(student.avg_score);
                        if (!trends[construct]) trends[construct] = [];
                        trends[construct].push({ x: timePoint, y: avg, student_id: student.canvas_user_id });
                        
                    }
                });

                setTrendData(trends);
                console.log("Trend data set:", trendData);
            }).catch((error) => {
                console.error("Error fetching trend data:", error);
            });
        }
    }, [allDates]);

    const studentNameMap = useMemo(() => {
        const map = {};
        studentQuestionAverage.forEach((d) => {
            map[d.canvas_user_id] = d.student_name;
        });
        return map;
    }, [studentQuestionAverage]);

    const labeledStats = Object.entries(studentClickStats).map(([id, stat]) => {
        const realName = studentNameMap[id] ?? "Unnamed";
        const alias = studentAliasMap[realName] ?? realName;

        return {
            canvas_user_id: id,
            realName: realName,
            name: alias,
            ...stat
        };
    });

    console.log("labeledStats:", labeledStats);

    const uniqueStudentNames = [
        ...new Set(studentQuestionAverage.map((d) => d.student_name))
    ];

    const handleSliderChange = (event, newValue) => setDateRange(newValue);

    const selectedCompletion = studentQuizCompletion?.find(
        (d) => d.student_name === selectedUser
        
    );

   

    const selectedCanvasUserId = Object.entries(studentNameMap).find(
        ([canvasUserId, name]) => name === selectedUser
    )?.[0];

    const selectedLatestClick = studentLatestClick?.find(
        (d) => String(d.canvas_user_id) === selectedCanvasUserId
    );

    const filteredTrendData = useMemo(() => {
        if (!selectedCanvasUserId) return {};

        const filtered = {};
        for (const [construct, values] of Object.entries(trendData)) {
            filtered[construct] = values.filter(d => String(d.student_id) === String(selectedCanvasUserId));
        }
        return filtered;
    }, [trendData, selectedCanvasUserId]);

    return (
        <div className="wrapper">
            <h1 className="title">INDIVIDUAL STUDENT DATA</h1>
            <div className="section-content">
                {/* Left panel: controls */}
                <div className="control-panel">

                    {allDates.length === 2 && dateRange[0] && dateRange[1] &&(
                        <>
                            <Typography variant="subtitle1" gutterBottom>Select time range of submissions:</Typography>
                            <div className ="slider-wrapper">
                                <Slider
                                    value={dateRange}
                                    onChange={handleSliderChange}
                                    valueLabelDisplay="auto"
                                    min={allDates[0]}
                                    max={allDates[1]}
                                    step={24 * 60 * 60 * 1000} // 1 day
                                    valueLabelFormat={(ts) => dayjs(ts).format("MMM D")}
                                />
                            </div>
                        </>
                    )}

                    <Typography variant="subtitle1" gutterBottom><strong>Select a student:</strong></Typography>
                    <Select
                        value={selectedUser}
                        onChange={(e) => setSelectedUser(e.target.value)}
                        style={{ width: '100%', marginBottom: '2rem' }}
                    >
                        {uniqueStudentNames.map((student_name) => (
                            <MenuItem key={student_name} value={student_name}>{studentAliasMap[student_name] || student_name}</MenuItem>
                        ))}
                    </Select>

                    
                </div>

                {/* Right panel: bar chart */}
                <div className="chart-panel">
                    <h2 className="section-title">1. WebCampus Status</h2>
                    <div className="info-box">
                        <p><strong>Current Score: </strong>
                            {selectedCompletion?.percent_answered != null
                                ? `${selectedCompletion.percent_answered}%`
                                : 'Loading...'}
                        </p>
                        <p>
                            <strong>Last Activity: </strong>
                            {selectedLatestClick?.latest_timestamp
                                ? dayjs(selectedLatestClick.latest_timestamp).format("MM/DD/YYYY HH:mm:ss [UTC]")
                                : 'Loading...'}
                        </p>
                    </div>
                    <p>*The data reflects the most recently refreshed information from our data source. There may occasionally be a minor delay compared to the live course information on WebCampus.</p>
                    <h2 className="section-title">2. Aggregated Survey Data</h2>
                    
                    
                    <div className="radar-wrapper">
                        <RadarChart 
                            data={studentQuestionAverage} 
                            selectedStudent={selectedUser} 
                            avg={questionAverage}
                        />
                    </div>
                </div>
            </div>

            <div className="section-content">
                {/* Left panel: click stats explanation */}
                <div className="control-panel">
                    <p><strong>Glossary:</strong></p>
                    <ul>
                        <li>
                            <strong>Breadth:</strong> The coverage of clicks on major WebCampus tools such as assignments, quizzes, discussions. A higher score means a <u>student has covered more course content</u>.
                        </li>
                        <li>
                            <strong>Depth:</strong> The density of clicks on major WebCampus tools such as assignments, quizzes, and discussions.
                            A higher score means a <u>student has clicked often on tools</u>.
                        </li>
                        <li>
                            <strong>Pattern:</strong> The sequential order of clicks on major WebCampus tools such as assignments, quizzes, and discussions.
                            A higher score means a <u>student navigates logically through course materials</u>.
                        </li>
                    </ul>
                </div>
                {/* Right panel: click stats table */}
                <div className="chart-panel">
                    <h2 className="section-title">3. WebCampus Click Data</h2>
                    <p>Standardized scores are shown below to indicate the activity patterns in comparison with their classmates. Class average is set to 0. A negative score means it's below the class average and a positive value means above it.</p>
                    <div className="chart-wrapper">
                        <IndividualClickBarChart labeledStats={labeledStats} selectedId={selectedUser} />
                    </div>
                </div>
            </div>

            <div className="section-content">
                <div className="chart-panel">
                    <h2 className="section-title">4. Survey Data Trends</h2>
                    <p>The charts below show the selected student's changes in each measure. You can also use this chart to check the number of data points collected for the student and identify any excessive missing data. The numbers on the x-axis represent the submitted date of the surveys. You can click on each measure's legend to mute or unmute it. Hover over a data point to see the exact score.</p>
                    {selectedUser ? (
                        <TrendChart trendData={filteredTrendData} />
                        ) : (
                        <p>Please select a student to view trend data.</p>
                    )}
                </div>
                
            </div>
        </div>
    )
};

export default Individuals;